import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { formatExerciseDuration } from '../../../utils/helper';
import ConfirmPopup from '../../common/modals/ConfirmPopup';

function reorder(list, startIndex, endIndex) {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
}

export default function WorkoutDraggableList({
  items,
  setItems,
  droppableId,
  heading,
  onSubmit,
  type = 'exercise', // 'exercise' or 'stretch'
  onDelete,
  onEdit, // New prop for edit functionality
  hideSubmitButton = false // New prop to hide submit button
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  function onDragEnd(result) {
    if (!result.destination) return;
    const reorderedItems = reorder(items, result.source.index, result.destination.index);

    // Update sequence numbers after reordering
    const updatedItems = reorderedItems.map((item, index) => ({
      ...item,
      sequence: index + 1
    }));

    setItems(updatedItems);

    // Auto-submit on drag if onSubmit is provided and hideSubmitButton is true
    if (hideSubmitButton && onSubmit) {
      const body = updatedItems.map((item, index) => ({
        exercise_id: item.exercise_id || item._id,
        sequence: index + 1
      }));
      onSubmit(body);
    }
  }

  const handleSubmit = () => {
    // Prepare data with updated sequence order
    const body = items.map((item, index) => ({
      exercise_id: item.exercise_id || item._id,
      sequence: index + 1
    }));

    onSubmit?.(body);
  };

  const getItemId = (item) => item._id || item.exercise_id;

  const handleDelete = (itemId) => {
    setPendingDeleteId(itemId);
    setConfirmOpen(true);
  };

  const confirmDelete = () => {
    const updatedItems = items
      .filter((item) => getItemId(item) !== pendingDeleteId)
      .map((item, index) => ({
        ...item,
        sequence: index + 1
      }));

    setItems(updatedItems);
    onDelete?.(pendingDeleteId);
    setConfirmOpen(false);
    setPendingDeleteId(null);
  };

  const renderItem = (item, idx) => {
    return (
      <div className="bg-white p-3 mb-2 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            {/* Drag Handle */}
            <div className="cursor-move text-gray-400">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z"></path>
              </svg>
            </div>

            {/* Exercise Image */}
            {item.image_url && (
              <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                <img
                  src={item.image_url}
                  alt={item.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}

            {/* Item Details */}
            <div className="flex-1">
              <h4 className="text-base font-medium text-gray-900">{item.name}</h4>
              {item.exercise_duration && (
                <p className="text-sm text-gray-500 mt-0.5">
                  {formatExerciseDuration(item.exercise_duration)}
                </p>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 ml-3">
            {/* Edit Button */}
            {/* {onEdit && (
              <button
                type="button"
                onClick={() => onEdit(item)}
                className="text-gray-400 hover:text-blue-500 p-1 rounded-full transition-colors"
                title="Edit"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </button>
            )} */}
            {/* Delete Button */}
            <button
              type="button"
              onClick={() => handleDelete(getItemId(item))}
              className="text-gray-400 hover:text-red-500 p-1 rounded-full transition-colors"
              title="Delete"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="mt-6">
      {heading && (
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{heading}</h3>
          <p className="text-sm text-gray-500 mt-1">
            Drag and drop to reorder {type === 'stretch' ? 'stretches' : 'exercises'}
          </p>
          <hr className="mt-3 border-gray-300" />
        </div>
      )}

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId={droppableId}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`max-h-96 overflow-y-auto rounded-lg p-2 ${
                snapshot.isDraggingOver ? 'bg-blue-50' : 'bg-gray-50'
              }`}
            >
              {items.map((item, idx) => (
                <Draggable key={getItemId(item)} draggableId={String(getItemId(item))} index={idx}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className={snapshot.isDragging ? 'opacity-50' : ''}
                    >
                      {renderItem(item, idx)}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {onSubmit && !hideSubmitButton && (
        <button
          type="button"
          className="btn-primary mt-4"
          onClick={handleSubmit}
        >
          Update Order
        </button>
      )}
      <ConfirmPopup
        open={confirmOpen}
        setOpen={setConfirmOpen}
        setAccepted={confirmDelete}
        message={`Are you sure you want to remove this ${type}?`}
      />
    </div>
  );
}
